import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useRouteMatch } from 'react-router-dom';
import { Card, Table, Tag, Statistic, Row, Col, message } from 'antd';
import api from '../../services/api';
import { Loading } from '../../components/UI';
import HealthCard from './HealthCard';

const linkStyle = {
  display: 'block',
  textDecoration: 'none',
  color: 'inherit',
  cursor: 'pointer',
  borderRadius: 4,
  padding: 4,
  margin: -4,
  transition: 'background-color 0.2s',
};

const StatesSummary = ({ surveyName, health }) => {
  const match = useRouteMatch();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSummary();
  }, [surveyName]);

  const loadSummary = async () => {
    try {
      const res = await api.fetcher({
        path: `/surveys/${encodeURIComponent(surveyName)}/states/summary`
      });
      const data = await res.json();
      setSummary(data.summary || []);
    } catch (err) {
      message.error('Failed to load states summary');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading>Loading states summary...</Loading>;

  // Compute aggregate stats
  const totalParticipants = summary.reduce((sum, row) => sum + parseInt(row.count, 10), 0);

  const stateCounts = summary.reduce((acc, row) => {
    acc[row.current_state] = (acc[row.current_state] || 0) + parseInt(row.count, 10);
    return acc;
  }, {});

  // State color mapping for consistent visual indicators
  const stateColors = {
    START: 'blue',
    RESPONDING: 'green',
    QOUT: 'cyan',
    END: 'default',
    BLOCKED: 'red',
    ERROR: 'red',
    WAIT_EXTERNAL_EVENT: 'orange',
    USER_BLOCKED: 'magenta',
  };

  const columns = [
    {
      title: 'Form',
      dataIndex: 'current_form',
      key: 'current_form',
      sorter: (a, b) => a.current_form.localeCompare(b.current_form),
    },
    {
      title: 'State',
      dataIndex: 'current_state',
      key: 'current_state',
      render: (state) => (
        <Tag color={stateColors[state] || 'default'}>
          {state}
        </Tag>
      ),
    },
    {
      title: 'Count',
      dataIndex: 'count',
      key: 'count',
      align: 'right',
      render: (count, record) => (
        <Link
          to={`${match.url}/list?state=${encodeURIComponent(record.current_state)}&form=${encodeURIComponent(record.current_form)}`}
          style={{ color: '#1890ff' }}
        >
          {parseInt(count, 10).toLocaleString()}
        </Link>
      ),
      sorter: (a, b) => parseInt(a.count, 10) - parseInt(b.count, 10),
      defaultSortOrder: 'descend',
    },
  ];

  return (
    <div>
      <HealthCard findings={health && health.findings} />

      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Link to={`${match.url}/list`} style={linkStyle}>
              <Statistic
                title="Total Participants"
                value={totalParticipants}
              />
            </Link>
          </Col>
          {Object.entries(stateCounts).map(([state, count]) => (
            <Col span={6} key={state}>
              <Link to={`${match.url}/list?state=${encodeURIComponent(state)}`} style={linkStyle}>
                <Statistic
                  title={
                    <span>
                      {state} <Tag color={stateColors[state] || 'default'} />
                    </span>
                  }
                  value={count}
                />
              </Link>
            </Col>
          ))}
        </Row>
      </Card>

      <Card title="State Breakdown by Form">
        <Table
          columns={columns}
          dataSource={summary}
          rowKey={(record) => `${record.current_form}-${record.current_state}`}
          pagination={{ pageSize: 20 }}
        />
      </Card>
    </div>
  );
};

StatesSummary.propTypes = {
  surveyName: PropTypes.string.isRequired,
  health: PropTypes.shape({
    findings: PropTypes.arrayOf(PropTypes.object),
    notices: PropTypes.arrayOf(PropTypes.object),
  }),
};

StatesSummary.defaultProps = {
  health: null,
};

export default StatesSummary;
